import { KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { format } from 'date-fns-tz';

import ArticlePlaceholder from 'components/ui/article-placeholder';
import Box from 'components/ui/box';
import CustomSelect from 'components/custom-select';
import ImgFeedback from 'assets/img/comportamento/bg-missoes-not-found.png';
import { RiArrowLeftSLine } from 'react-icons/ri';
import { TfiMedallAlt } from 'react-icons/tfi';
import { PiThumbsUpLight, PiHandsClappingLight } from 'react-icons/pi';
import { FaRegGrinStars, FaCoins } from 'react-icons/fa';
import { BiMinus, BiUpvote } from 'react-icons/bi';
import { BsChatText, BsPlusLg } from 'react-icons/bs';
import { HiOutlineHandThumbDown, HiOutlineHandThumbUp } from 'react-icons/hi2';
import { subMinutes } from 'date-fns';
import { Countdown } from 'components/countdown';
import { ExportButton } from 'components/generate-xcel';
import ModalChangeIndicator from 'components/modal-change-indicator';
import { TaskItemMad } from 'components/task-item-mad';
import { UserAvatar } from 'components/user-avatar';
import { NotFound } from 'pages/NotFound';
import { getTasksManagerMad } from 'services/dashboard.service';
import { GetVersion } from 'services/utils';
import { getFutureCalendar, getMonthNameFromString } from 'utils/helpers';
import { CheckManagerType } from 'utils/strings';

import Header from '../../components/Header';
import ModalAlert from '../../components/ModalAlert';
import ModalCod5 from '../../components/ModalCod5';
import SelectDupla from './SelectDupla';

import { setFlowChoice } from '../shared/eligibility';
import { FlowLoading } from '../shared/FlowLoading';
import { canUpdateIndicator } from '../shared/indicator';
import { getPlayerInfoWithPairs } from '../shared/playerInfoCache';
import { FlowTabs } from '../shared/FlowTabs';
import { matchesSearch } from '../shared/search';
import { useCycleStage } from '../shared/useCycleStage';
import { useFlowEligibility } from '../shared/useFlowEligibility';
import { useLateralidade } from '../shared/useLateralidade';
import { useServerTime } from '../shared/useServerTime';
import { useTasksData } from '../shared/useTasksData';
import { buildMadContext, MadRuleContext, madUserCodEtapa } from './context';
import { computeQuotas, filterStageData } from './quotas';
import { optionsPairs, filterByPair } from './pairs';
import {
  canApproveParticipantsCount,
  canAward,
  canDouble,
  canEdit,
  canEvaluate,
  canPromote,
  canReset,
  getPlayerStep,
  getTaskStatuses,
  getTaskStepLocal,
  hasTaskFeedbackForUser,
  isPitStopAssessor,
} from './rules';
import { useMadActions } from './useMadActions';

// Página do fluxo MAD (dupla de liderança) — espelho de normal/index.tsx restrito ao
// universo exclusivamente MAD (aggregate tasks_manager_mad). Além das abas Diretoria |
// MAD | Rede (FlowTabs), o MAD tem o SELETOR DE PAR da dupla (COT-03); a divisão
// Normal×MAD é feita pelas rotas (ELE-02/04) e MAD é a página atual, então as abas
// Diretoria e Rede navegam para o fluxo Normal.

export function FluxoMAD() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const { user, cyclesExtended, realCycle, cycleSelected, setCycleSelected, userInfo } = useAuth();

  const { realDatetime } = useServerTime();
  const { internalUser, isVacationManager } = useLateralidade(user);

  // Fase trazida pelo FlowTabs na troca de fluxo. Capturada na MONTAGEM: logo depois a
  // página re-navega para a própria rota (efeito de troca de ciclo), zerando location.state.
  const [requestedStage] = useState<string | undefined>(() => (location.state as { stage?: string } | null)?.stage);

  // Seletor de par da dupla (COT-03) — substitui as tabs diretoria×rede do Normal.
  // undefined/próprio = minhas realizações; parceiro = as realizações que ele tocou.
  const [pairSelected, setPairSelected] = useState<string | undefined>();

  const [pageLoading, setPageLoading] = useState(false);
  const [gestor, setIsGestor] = useState(false);
  const [playerInfo, setPlayerInfo] = useState<any>();

  const [filteredData, setFilteredData] = useState<any[]>();
  const [tasksByFilter, setTasksByFilter] = useState<any[]>();
  const [tasksStatus, setTasksStatus] = useState<string[]>([]);
  const [selectedtasksStatus, setSelectedTasksStatus] = useState<string>();
  const [selectedFeedbackStatus, setSelectedFeedbackStatus] = useState<string>();
  const [searchText, setSearchText] = useState<string>();
  const [showConcurrentActionModal, setShowConcurrentActionModal] = useState<string | undefined>();
  // Realização com o modal de ajuste de indicador aberto. Guarda o `owner` junto porque
  // a lista de indicadores é a do DONO, não a do gestor que está com o modal aberto.
  const [indicatorTask, setIndicatorTask] = useState<{ id: string; owner?: string } | undefined>();

  const [showMore, setShowMore] = useState(false);
  const [calendarElementsIndexOf] = useState<number>(5);
  const [calendarNumberOfElements, setCalendarNumberOfElements] = useState(5);
  const [calendarShowMore, setCalendarShowMore] = useState(false);

  // PARIDADE: o bônus "Carta Reconhecer+" nunca é carregado no monólito (verifyBonus só
  // vive em código comentado). Mantemos indefinido para não mudar comportamento.
  const [bonus] = useState<any>(undefined);

  const { data: rawData, loading, setLoading, reload } = useTasksData(getTasksManagerMad);

  // Nível efetivo p/ calendário — no MAD a composição pela dupla é integral (madUserCodEtapa).
  const codEtapaEfetivo = useMemo(() => {
    if (!playerInfo) return undefined;

    return madUserCodEtapa(buildMadContext({ playerInfo, isVacationManager }), playerInfo);
  }, [isVacationManager, playerInfo]);

  const {
    stages,
    activeStageObj,
    getActiveEvaluationStep,
    currentCycle,
    setCurrentCycle,
    currentStage,
    setCurrentStage,
    optionsCycle,
    activeCycleSelected,
    setActiveCycleSelected,
    elementsIndexOf,
    numberOfElements,
    setNumberOfElements,
    configCycleAndStage,
  } = useCycleStage({
    cyclesExtended: cyclesExtended as any,
    cycleSelected,
    realDatetime,
    codEtapaEfetivo,
    requestedStage,
  });

  // Elegibilidade Normal×MAD do gestor exibido NO CICLO SELECIONADO — alimenta as abas
  // (ELE-04) e tira o gestor daqui se o ciclo escolhido não tiver universo MAD.
  const eligibility = useFlowEligibility({
    playerId: internalUser?._id,
    cycleId: cycleSelected?._id,
    tasks: rawData,
    flow: 'mad',
    currentStage,
  });

  // Com as abas de volta para dentro do card (e não mais no submenu do header), o card
  // precisa existir sempre que houver fluxo para trocar — senão o gestor elegível aos
  // dois ficaria preso neste fluxo fora da janela de apreciação.
  const canSwitchFlow = !!eligibility?.normal && !!eligibility?.mad;

  // Contexto-base das regras (sem cotas) — espelha os estados homônimos do monólito.
  const baseCtx: MadRuleContext = useMemo(
    () =>
      buildMadContext({
        playerInfo,
        userInfo: userInfo as any,
        internalUser: internalUser as any,
        realDatetime,
        activeStageObj: activeStageObj as any,
        getActiveEvaluationStep,
        cyclesExtended: cyclesExtended as any,
        realCycle: realCycle as any,
        isVacationManager,
        bonus,
      }),
    [
      activeStageObj,
      bonus,
      cyclesExtended,
      getActiveEvaluationStep,
      internalUser,
      isVacationManager,
      playerInfo,
      realCycle,
      realDatetime,
      userInfo,
    ],
  );

  // Enriquecimento com labels de status (UI-08).
  const data = useMemo(() => {
    if (!Array.isArray(rawData)) return rawData;

    return rawData.map((task: any) => ({
      ...task,
      statuses: getTaskStatuses(baseCtx, task) || [],
    }));
  }, [baseCtx, rawData]);

  // Port de getUserInfo (index.tsx:2058-2072): playerInfo do gestor exibido, com pares.
  // Cacheado por sessão (playerInfoCache): a troca de aba remonta o fluxo e a guarda de
  // render não deve esperar rede de novo pelo mesmo gestor.
  const getUserInfo = useCallback(async () => {
    try {
      if (playerInfo && playerInfo.chave === internalUser?._id) return;
      if (!internalUser?._id) return;

      const playerInfoResult = await getPlayerInfoWithPairs(internalUser._id);

      if (playerInfoResult) {
        setIsGestor(playerInfoResult.is_manager);
        setPlayerInfo(playerInfoResult);
        setLoading(false);
      }
    } catch (e) {
      console.log(e);
    }
  }, [internalUser?._id, playerInfo, setLoading]);

  // Port do loadData (index.tsx:2085-2148) com os parâmetros da fase fechados.
  const loadData = useCallback(
    async (taskId?: string) => {
      if (!!activeStageObj) {
        const hasTaskId = typeof taskId === 'string' && taskId.length > 0;

        if (!hasTaskId) {
          setTasksStatus([]);
          setSelectedFeedbackStatus(undefined);
          setSelectedTasksStatus(undefined);
          setSearchText(undefined);
        }

        return reload(
          {
            player: internalUser?.extra.id,
            start: activeStageObj.date_start,
            end: activeStageObj.date_finish,
            step: (activeStageObj as any).step,
          },
          taskId,
        );
      }

      return typeof taskId === 'string' && taskId.length > 0 ? null : [];
    },
    [activeStageObj, internalUser?.extra?.id, reload],
  );

  // Port do memo isDirectManager (index.tsx:139-147).
  const isDirectManager = useMemo(
    () =>
      isVacationManager &&
      userInfo &&
      playerInfo &&
      ((userInfo as any)?._id === playerInfo?.gestor ||
        (Array.isArray((userInfo as any)?.mad_dupla) &&
          (userInfo as any)?.mad_dupla?.some((md: any) => md._id && md._id === playerInfo?.gestor))),
    [isVacationManager, playerInfo, userInfo],
  );

  // Port de index.tsx:699-708.
  const isOriginalManager = useMemo(() => {
    if (!filteredData?.length) return false;
    return !!filteredData.find(
      x =>
        x.original_manager &&
        (x.original_manager === playerInfo?._id ||
          (Array.isArray(playerInfo?.mad_dupla) &&
            playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(x.original_manager)))),
    );
  }, [filteredData, playerInfo]);

  // Port de index.tsx:710-721.
  const isOriginalManagerCod5 = useMemo(() => {
    if (!filteredData?.length || !playerInfo) return false;
    return !!filteredData.find(
      x =>
        x.original_manager &&
        (x.original_manager === playerInfo?._id ||
          (Array.isArray(playerInfo?.mad_dupla) &&
            playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(x.original_manager)))) &&
        madUserCodEtapa(baseCtx) &&
        madUserCodEtapa(baseCtx) === 5,
    );
  }, [baseCtx, filteredData, playerInfo]);

  // Port de index.tsx:723-728.
  const handleLinkSpaceKeyDown = useCallback((event: KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      event.currentTarget.click();
    }
  }, []);

  // Opções do seletor de par (COT-03).
  const pairOptions = useMemo(
    () => optionsPairs(playerInfo).map(option => ({ label: option.label ?? '', value: option.value })),
    [playerInfo],
  );

  // Port do clarifyingText (index.tsx:753-933) — madUserCodEtapa (composição integral).
  const clarifyingText = useMemo(() => {
    if (
      !playerInfo ||
      !Array.isArray(filteredData) ||
      !madUserCodEtapa(baseCtx) ||
      typeof madUserCodEtapa(baseCtx) !== 'number' ||
      (madUserCodEtapa(baseCtx) as number) < 1 ||
      (madUserCodEtapa(baseCtx) as number) > 5
    )
      return <></>;

    const _tasks = [...(filteredData as any[])];
    let stepMsg;

    if (madUserCodEtapa(baseCtx) === 4 && !!isOriginalManager) {
      if (typeof getActiveEvaluationStep === 'number') {
        if (getActiveEvaluationStep === 2) {
          stepMsg = 0;
        } else if (getActiveEvaluationStep === 3) {
          stepMsg = 1;
        } else {
          stepMsg = 2;
        }
      } else {
        stepMsg = 2;
      }
    } else if (madUserCodEtapa(baseCtx) === 5 && !!isOriginalManager) {
      if (typeof getActiveEvaluationStep === 'number') {
        if (getActiveEvaluationStep === 2) {
          stepMsg = 0;
        } else if (getActiveEvaluationStep === 3) {
          stepMsg = 1;
        } else if (getActiveEvaluationStep === 4 || getActiveEvaluationStep === 4.1) {
          stepMsg = 2;
        } else {
          stepMsg = 3;
        }
      } else {
        stepMsg = 3;
      }
    } else {
      if (madUserCodEtapa(baseCtx) === 2) {
        stepMsg = 0;
      } else if (madUserCodEtapa(baseCtx) === 4) {
        stepMsg = 2;
      } else if (madUserCodEtapa(baseCtx) === 5) {
        stepMsg = 3;
      } else if (madUserCodEtapa(baseCtx) === 1 || madUserCodEtapa(baseCtx) === 3) {
        if (_tasks.length) {
          let hasLevel1 = false;

          _tasks.forEach(task => {
            if (
              typeof getTaskStepLocal(task) === 'number' &&
              (getTaskStepLocal(task) as number) >= 3 &&
              ((typeof getActiveEvaluationStep === 'number' && getActiveEvaluationStep > 2) || getActiveEvaluationStep === null)
            ) {
              hasLevel1 = true;
            }
          });

          stepMsg = hasLevel1 ? 1 : 0;
        } else {
          stepMsg = 0;
        }
      }
    }

    if (typeof stepMsg === 'number') {
      if (stepMsg === 1) {
        return (
          <>
            <p tabIndex={0} className="text-dandelion text-lg font-semibold block mb-4">
              Apreciação das realizações validadas
            </p>
            <p tabIndex={0} className="text-white text-base font-thin mb-1">
              Cada realização recebida por você foi <strong className="font-bold">validada</strong> por funcionário pertencente à
              função gerencial e recebeu 300 (trezentos) pontos.
            </p>
            <p tabIndex={0} className="text-white text-base font-thin mb-1">
              Nesta apreciação, você poderá BONIFICAR todas as realizações que considerar mais relevantes em seu âmbito de
              atuação, e PROMOVER ao próximo nível, até 10% (dez por cento) das realizações bonificadas que você considerar
              extraordinárias.
            </p>
            <p tabIndex={0} className="text-white text-base font-thin mb-1">
              A realização <strong className="font-bold">bonificada</strong> passará a 600 (seiscentos) pontos e a{' '}
              <strong className="font-bold">promovida</strong> terá 100 (cem) pontos adicionais.
            </p>

            <p tabIndex={0} className="text-white text-base font-light">
              <span className="text-electric-blue">Lembre-se</span> que ao deixar feedback para{' '}
              <strong className="font-bold">as entregas</strong> recebidas você promove o engajamento e o desenvolvimento de sua
              equipe.
            </p>
          </>
        );
      } else if (stepMsg === 2) {
        return (
          <>
            <p tabIndex={0} className="text-dandelion text-lg font-semibold block mb-4">
              Apreciação das entregas promovidas pelo nível 1
            </p>
            <p tabIndex={0} className="text-white text-base font-thin mb-1">
              Cada realização recebida por você foi <b>promovida</b> por funcionário pertencente à função gerencial e recebeu 700 (setecentos) pontos.
            </p>
            <p tabIndex={0} className="text-white text-base font-thin mb-1">
              Nesta apreciação, você poderá BONIFICAR todas as realizações que considerar relevantes em seu âmbito de atuação, e PROMOVER ao próximo nível, até 20% (vinte por cento) das realizações bonificadas que você considerar extraordinárias.
            </p>
            <p tabIndex={0} className="text-white text-base font-thin mb-1">
              A realização <strong className="font-bold">bonificada</strong> passará a 1200 (mil e duzentos) pontos e a <strong className="font-bold">promovida</strong> terá 100 (cem) pontos adicionais.
            </p>

            <p tabIndex={0} className="text-white text-base font-light">
            Lembre-se que ao deixar feedback para as entregas recebidas você promove o engajamento e o desenvolvimento de sua equipe.
            </p>
          </>
        );
      } else if (stepMsg === 3) {
        return (
          <>
            <p tabIndex={0} className="text-dandelion text-lg font-semibold block mb-4">
              Apreciação das entregas promovidas pelo nível 2
            </p>
            <p tabIndex={0} className="text-white text-base font-thin mb-1">
              Cada realização recebida por você foi promovida por funcionário responsável pela apreciação nível 2 e recebeu 1.300
              (mil e trezentos) pontos.
            </p>
            <p tabIndex={0} className="text-white text-base font-thin mb-1">
              Nesta apreciação, você poderá BONIFICAR VALEU para 100% das realizações, BONIFICAR MANDOU BEM para até 50%
              (cinquenta por cento) das realizações e BONIFICAR UAU para até 25% (vinte e cinco por cento) das realizações que
              você considerar extraordinárias.
            </p>
            <p tabIndex={0} className="text-white text-base font-thin mb-1">
              A realização <strong className="font-bold">Bonificada Valeu</strong> passará a 1.500 (mil e quinhentos) pontos, a
              realização <strong className="font-bold">Bonificada Mandou Bem</strong> passará a 2.500 (dois mil e quinhentos)
              pontos e a realização <strong className="font-bold">Bonificada UAU</strong> passará a 5.000 (cinco mil) pontos.
            </p>

            <p tabIndex={0} className="text-white text-base font-light">
              E que tal deixar um feedback para a equipe? Afinal, reconhecimento nunca é demais!
            </p>
          </>
        );
      } else {
        return (
          <>
            <p tabIndex={0} className="text-dandelion text-lg font-semibold block mb-4">
              VALIDAÇÃO das entregas cadastradas por sua equipe
            </p>
            <p tabIndex={0} className="text-white text-base font-thin mb-1">
              Cada realização recebida por você foi cadastrada por um de seus liderados.
            </p>
            <p tabIndex={0} className="text-white text-base font-thin mb-1">
              Para VALIDAR a entrega, verifique se há alinhamento com os indicadores e desafios da equipe, se não há informações
              sigilosas, duplicidade e ou conteúdo inadequado, caso contrário você pode DEVOLVER a realização.
            </p>
            <p tabIndex={0} className="text-white text-base font-thin mb-1">
              Ao ser devolvida, a realização volta para o responsável pelo cadastramento e se for corrigida, dentro do período de
              validação, você poderá analisá-la novamente.
            </p>
            <p tabIndex={0} className="text-white text-base font-thin mb-1">
              A realização <strong className="font-bold">validada</strong> recebe 300 (trezentos) pontos.
            </p>

            <p tabIndex={0} className="text-white text-base font-light">
              <span className="text-electric-blue">Lembre-se</span> que ao deixar feedback para{' '}
              <strong className="font-bold">as entregas</strong> recebidas você promove o engajamento e o desenvolvimento de sua
              equipe.
            </p>
          </>
        );
      }
    } else {
      return <></>;
    }
  }, [baseCtx, filteredData, getActiveEvaluationStep, isOriginalManager, playerInfo]);

  // Port de index.tsx:948-960.
  const getFullCalendar = useMemo(() => {
    if (!cyclesExtended || !cycleSelected) return null;

    const cycleActive = cyclesExtended.find(x => x._id === cycleSelected?._id);

    if (cycleActive) {
      const calendar = getFutureCalendar(cycleActive?.steps, realDatetime);
      return calendar;
    }

    return null;
  }, [cycleSelected, cyclesExtended, realDatetime]);

  // Port de hasRedeMAD (index.tsx:3156-3178) — no MAD a flag é usada só para gate de
  // exibição do painel de cotas (as tabs diretoria/rede não existem aqui).
  const hasRedeMAD: { rede: boolean; mad: boolean } | undefined = useMemo(() => {
    if (!Array.isArray(data) || !data.length || !playerInfo || !activeStageObj) return undefined;

    const obj = { rede: false, mad: false };

    const itemsByStage = data?.filter(item => {
      return item.created >= (activeStageObj as any)?.date_start && item.created <= (activeStageObj as any)?.date_finish;
    });

    if (itemsByStage.length) {
      if (
        madUserCodEtapa(baseCtx) &&
        madUserCodEtapa(baseCtx) === 5 &&
        (activeStageObj as any)?.type &&
        (activeStageObj as any)?.type === 'semester'
      ) {
        obj.rede = !!itemsByStage.filter(
          (x: { prosseguir_gst: string; owner_tipo_classificacao: string }) =>
            (!!x.prosseguir_gst && x.prosseguir_gst === 'approved') ||
            (!!x.owner_tipo_classificacao && x.owner_tipo_classificacao === 'UNIDADE TÁTICA'),
        ).length;
      }

      obj.mad = !!itemsByStage.filter(x => !!x.mad || (Array.isArray(x.non_evaluator) && x.non_evaluator.length > 0)).length;
    }

    return obj;
  }, [activeStageObj, baseCtx, data, playerInfo]);

  // Port de tasksProsseguirAssessor (index.tsx:3142-3154) — dupla-aware: a task parada
  // comigo OU com meu parceiro (a dupla age como um).
  const tasksProsseguirAssessor: any[] = useMemo((): any[] => {
    if (!Array.isArray(filteredData) || !filteredData.length || !playerInfo) return [];

    const duplaIds = Array.isArray(playerInfo?.mad_dupla)
      ? playerInfo.mad_dupla
          .map((md: any) => md?._id)
          .filter(Boolean)
          .map(String)
      : [];

    const tasks = filteredData.filter(task => {
      if (
        !!task.prosseguir_idGestor &&
        (task.prosseguir_idGestor === playerInfo._id || duplaIds.includes(String(task.prosseguir_idGestor)))
      )
        return true;

      if (
        Array.isArray(task.prosseguir_managers) &&
        (task.prosseguir_managers.includes(playerInfo._id) ||
          task.prosseguir_managers.some((id: string) => duplaIds.includes(String(id))))
      )
        return true;

      return false;
    });

    return tasks;
  }, [filteredData, playerInfo]);

  // Universo por fase + par (COT-01/COT-03): filterStageData (pool MAD) então filterByPair.
  useEffect(() => {
    function updateData() {
      try {
        let stageData: any[] = filterStageData(baseCtx, data);

        stageData = filterByPair(stageData, pairSelected, baseCtx);

        const statuses: string[] = ['Selecione'];

        stageData.forEach((task: any) => {
          if (Array.isArray(task.statuses) && task.statuses.length) {
            task.statuses.forEach((_status: string) => {
              if (!statuses.find(x => x === _status)) {
                statuses.push(_status);
              }
            });
          }
        });

        setTasksStatus(statuses);

        setFilteredData(stageData);

        setLoading(false);
      } catch (e) {
        setFilteredData([]);
        setLoading(false);
      }
    }

    if (playerInfo) updateData();
  }, [pairSelected, baseCtx, data, playerInfo, setLoading]);

  // Cotas e universos da fase (COT-04..09) sobre o pool MAD filtrado por par.
  const quotas = useMemo(() => computeQuotas(baseCtx, Array.isArray(filteredData) ? filteredData : []), [baseCtx, filteredData]);

  // Contexto COMPLETO das regras: base + cotas.
  const ctx: MadRuleContext = useMemo(
    () => ({
      ...baseCtx,
      doubledTasks: quotas.doubledTasks,
      maxDoubledTasks: quotas.maxDoubledTasks,
      promotedTasks: quotas.promotedTasks,
      maxPromotedTasks: quotas.maxPromotedTasks,
      uauTasks: quotas.uauTasks,
      maxUauTasks: quotas.maxUauTasks,
      mandouBemTasks: quotas.mandouBemTasks,
      maxMandouBemTasks: quotas.maxMandouBemTasks,
    }),
    [baseCtx, quotas],
  );

  const {
    selectedDupla,
    setSelectedDupla,
    functionDupla,
    getAction,
    setGetAction,
    modalCod5,
    setModalCod5,
    handleAccept,
    handleStop,
    handleReject,
    handleDoubled,
    handlePromote,
    handleAward,
    handleReset,
    handleSubmitCod5,
    resolveDuplaSelection,
  } = useMadActions({
    ctx,
    data,
    filteredData,
    reload: loadData,
    setPageLoading,
    showConcurrentModal: setShowConcurrentActionModal,
    quotas,
    bonus,
    user,
    isVacationManager,
  });

  const functionsObject: any = {
    handleAccept,
    handlePromote,
  };

  // Filtro por feedback/status/texto (port de index.tsx:3966-3998, UI-05).
  useEffect(() => {
    if (Array.isArray(filteredData) && filteredData.length) {
      let _data = Array.isArray(filteredData) && filteredData.length ? filteredData : [];

      if (
        typeof selectedFeedbackStatus === 'string' &&
        selectedFeedbackStatus.length &&
        selectedFeedbackStatus.toLowerCase() !== 'selecione'
      ) {
        _data = _data.filter((task: any) => {
          const hasFeedback = hasTaskFeedbackForUser(baseCtx, task);
          return selectedFeedbackStatus === 'sim' ? hasFeedback : !hasFeedback;
        });
      }

      if (
        typeof selectedtasksStatus === 'string' &&
        selectedtasksStatus.length &&
        selectedtasksStatus.toLowerCase() !== 'selecione'
      ) {
        _data = _data.filter(x => !!x.statuses.includes(selectedtasksStatus));
      }

      if (typeof searchText === 'string' && searchText.trim().length) {
        const _search = searchText.replace(/ +/gi, ' ').trim().toLowerCase();
        _data = _data.filter(x => matchesSearch(x, _search));
      }

      setTasksByFilter(_data);
    } else {
      setTasksByFilter([]);
    }
  }, [baseCtx, filteredData, searchText, selectedFeedbackStatus, selectedtasksStatus]);

  const feedbackFilterOptions = useMemo(() => {
    const tasks = Array.isArray(filteredData) ? filteredData : [];
    const withFeedback = tasks.filter(task => hasTaskFeedbackForUser(baseCtx, task)).length;
    const withoutFeedback = tasks.length - withFeedback;

    return [
      {
        label: 'Selecione',
        value: 'selecione',
      },
      {
        label: `Com feedback (${withFeedback})`,
        value: 'sim',
      },
      {
        label: `Sem feedback (${withoutFeedback})`,
        value: 'nao',
      },
    ];
  }, [baseCtx, filteredData]);

  const selectedFeedbackOption = useMemo(
    () => feedbackFilterOptions.find(option => option.value === selectedFeedbackStatus) || null,
    [feedbackFilterOptions, selectedFeedbackStatus],
  );

  const taskStatusOptions = useMemo(() => {
    const tasks = Array.isArray(filteredData) ? filteredData : [];

    return (
      tasksStatus?.map(status => ({
        label: `${status}${status !== 'Selecione' ? ` (${tasks.filter(x => !!x.statuses.includes(status)).length})` : ''}`,
        value: status,
      })) || []
    );
  }, [filteredData, tasksStatus]);

  const selectedTaskStatusOption = useMemo(
    () => taskStatusOptions.find(option => option.value === selectedtasksStatus) || null,
    [selectedtasksStatus, taskStatusOptions],
  );

  // Port de index.tsx:4042-4047.
  useEffect(() => {
    if (!internalUser && !playerInfo) return;

    setLoading(true);
    getUserInfo();
  }, [getUserInfo, internalUser, playerInfo, setLoading]);

  // Port de index.tsx:4049-4055.
  useEffect(() => {
    if (!gestor) return;

    setLoading(true);

    loadData();
  }, [gestor, loadData, setLoading]);

  // Port de index.tsx:4057-4065.
  useEffect(() => {
    if (!stages) return;

    if (stages.length <= numberOfElements) {
      setShowMore(false);
    } else {
      setShowMore(true);
    }
  }, [numberOfElements, stages]);

  // Port de index.tsx:4067-4075.
  useEffect(() => {
    if (!getFullCalendar) return;

    if (getFullCalendar.length <= calendarNumberOfElements) {
      setCalendarShowMore(false);
    } else {
      setCalendarShowMore(true);
    }
  }, [calendarNumberOfElements, getFullCalendar]);

  // Port de index.tsx:4077-4091 — recarrega ao voltar da tela de detalhe.
  useEffect(() => {
    try {
      const locationState: any = location.state;

      if (locationState && locationState.direction) {
        if (locationState.direction === 'back') {
          if (gestor) {
            loadData();
          }
        }
      }
    } catch (e) {
      console.log(e);
    }
  }, [gestor, loadData, location]);

  // Port do reload:lateralidade (index.tsx:4118-4122).
  useEffect(() => {
    function handleLateralidadeLoading() {
      setLoading(true);
    }

    document.addEventListener('reload:lateralidade', handleLateralidadeLoading, false);

    return () => {
      document.removeEventListener('reload:lateralidade', handleLateralidadeLoading);
    };
  }, [setLoading]);

  // Port de index.tsx:4207-4211.
  useEffect(() => {
    if (!user || !cyclesExtended || !internalUser) return;

    configCycleAndStage();
  }, [configCycleAndStage, cyclesExtended, internalUser, user]);

  // Port de index.tsx:4227-4237 — troca de ciclo navega para a rota deste fluxo (mad).
  useEffect(() => {
    if (params?.id) return;

    if (cycleSelected) {
      if (cycleSelected?.version && cycleSelected?.version !== 'v3') {
        navigate(`/${cycleSelected.version}/minha-equipe/validar-apreciar-realizacao`);
      } else {
        navigate(`/minha-equipe/validar-apreciar-realizacao/mad`);
      }
    }
  }, [cycleSelected, navigate, params.id]);

  // Estar AQUI é a escolha: grava a visita para o guarda-chuva voltar ao MAD nesta
  // sessão (ELE-02) — não só o clique no switcher conta.
  useEffect(() => {
    setFlowChoice('mad');
  }, []);

  function getIndexByValue(array: any, value: string) {
    return array.findIndex((element: any) => element.value === value);
  }

  // Guardas de render — port de index.tsx:4239-4277. Em vez de tela em branco (null),
  // renderiza o shell da página com spinner enquanto user/ciclo/fase resolvem.
  if (
    !user ||
    !internalUser ||
    typeof gestor !== 'boolean' ||
    typeof currentCycle === 'undefined' ||
    typeof currentStage === 'undefined' ||
    !activeStageObj
  ) {
    return <FlowLoading />;
  }

  if (!gestor && !loading) {
    return (
      <div className="mx-auto container p-4 lg:p-10">
        <div className="flex flex-row items-center space-x-3 mb-16">
          <div>
            <Link to="../" tabIndex={-1}>
              <button className="rounded-full bg-purple flex justify-center items-center p-1">
                <RiArrowLeftSLine size={28} className="text-white" />
              </button>
            </Link>
          </div>
          <h1 tabIndex={0} className="text-lg lg:text-3xl py-1 rounded-full">
            Minha Equipe
          </h1>
        </div>

        <div className="border-2 border-electric-blue rounded-3xl p-8 bg-oxford-blue">
          <p tabIndex={0} className="text-dandelion text-lg font-semibold block text-center">
            Você não possui acesso à esta página.
          </p>
        </div>
      </div>
    );
  }

  if (user?.extra.rede) return <NotFound />;

  return (
    <>
      {!!pageLoading && (
        <div className="fixed z-40 w-full h-full top-0 left-0 block flex items-center justify-center bg-black bg-opacity-60">
          <div role="status" className="flex items-center justify-center flex-col space-y-2 text-white">
            <svg
              aria-hidden="true"
              className="w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span tabIndex={0} className="text-white">
              Carregando...
            </span>
          </div>
        </div>
      )}

      <main className="mx-auto container p-3 lg:p-10 overflow-hidden" aria-busy={loading}>
        <Header
          isRoot={true}
          onSubmitSearch={function (id: any): void {
            console.log(id);
          }}
        />

        {!!isVacationManager && !!internalUser && (
          <div className="border-2 border-electric-blue rounded-3xl px-8 py-6 mb-12 bg-rich-black bg-opacity-80 ">
            <p tabIndex={0} className="text-dandelion text-lg font-semibold block mb-3">
              Você está {madUserCodEtapa(baseCtx) && madUserCodEtapa(baseCtx) === 2 ? 'validando' : 'apreciando'} por:
            </p>

            <div className="flex flex-row items-center space-x-4">
              <div className="w-[44px] h-[44px]">
                <UserAvatar
                  data={{
                    image: internalUser.image,
                    ...internalUser.extra,
                  }}
                />
              </div>
              <p tabIndex={0}>
                <span className="text-xs font-thin">
                  <strong className="text-base font-semibold">{internalUser.name}</strong> ({internalUser._id})
                </span>
                <span className="block text-xs font-thin">
                  <span className="text-electric-blue uppercase">{internalUser.extra?.dependencia}</span> -{' '}
                  {internalUser.extra?.funcao}
                </span>
              </p>
              <Link
                to="/"
                className={`!ml-8 leading-none ring-2 ring-slate-blue w-full lg:w-auto flex flex-col lg:flex-row items-center bg-slate-blue text-center text-white hover:text-white hover:no-underline no-underline rounded py-2 px-3`}
                onKeyDown={handleLinkSpaceKeyDown}
              >
                SAIR
              </Link>
            </div>
          </div>
        )}

        <div
          className="border-2 border-electric-blue rounded-3xl px-8 py-6 mb-12 bg-oxford-blue"
          role="region"
          aria-labelledby="clarifying-heading"
        >
          <h1 tabIndex={0} id="clarifying-heading" className="sr-only">
            Orientações sobre apreciação de realizações
          </h1>
          {clarifyingText}
        </div>

        <div className="w-full flex flex-col lg:flex-row lg:justify-between">
          <div className="lg:w-[32.5%]">
            <div className="mb-5">
              <h2 tabIndex={0} className="text-xl text-white block mb-6 font-semibold">
                Histórico de Realizações
              </h2>

              <Box>
                {loading ? (
                  <div className="grid grid-cols-1 gap-3">
                    <ArticlePlaceholder />
                  </div>
                ) : (
                  <>
                    <div className="px-8 mb-8">
                      <div>
                        <p tabIndex={0} className="mb-2">
                          Escolha o ciclo que deseja acompanhar
                        </p>

                        {optionsCycle && (
                          <div className="mb-4">
                            <CustomSelect
                              placeholder="Clique para selecionar o ciclo"
                              options={optionsCycle}
                              onChange={(e: any) => {
                                setActiveCycleSelected(e.value);
                                setCurrentCycle(undefined);
                                setCurrentStage(undefined);

                                const selected = cyclesExtended?.find(x => x._id === e.value);

                                if (selected) {
                                  setCycleSelected({
                                    _id: selected._id,
                                    title: selected.title,
                                    version: GetVersion(selected.date_start),
                                  });
                                }
                              }}
                              value={
                                optionsCycle.find(x => x.value === cycleSelected?._id)
                                  ? optionsCycle.find(x => x.value === cycleSelected?._id)
                                  : optionsCycle[getIndexByValue(optionsCycle, activeCycleSelected)]
                              }
                            />
                          </div>
                        )}
                      </div>

                      <p tabIndex={0} className="text-white mb-8 text-base font-normal block">
                        Escolha a fase
                      </p>

                      <ul className="space-y-4 flex flex-col">
                        {stages &&
                          stages.slice(0, numberOfElements).map((cycle: any) => {
                            return (
                              <li key={cycle._id}>
                                <button
                                  className={`${
                                    currentStage === cycle._id ? 'text-alice-blue bg-slate-blue' : 'text-white'
                                  } text-sm flex items-center justify-center font-semibold uppercase ring-2 ring-slate-blue rounded w-full h-10`}
                                  onClick={() => setCurrentStage(cycle._id)}
                                  aria-label={`Selecionar fase ${cycle.title}`}
                                >
                                  {cycle.title}
                                </button>
                              </li>
                            );
                          })}

                        {!!stages && stages?.length > (elementsIndexOf as number) && (
                          <button
                            className="mt-4 mx-auto flex items-center text-grey-light justify-center lg:justify-start"
                            onClick={() =>
                              !showMore
                                ? setNumberOfElements(elementsIndexOf as number)
                                : setNumberOfElements(numberOfElements + 3)
                            }
                          >
                            {!showMore ? (
                              <>
                                <BiMinus className="mr-2 inline-block grey-dark" size={17} />
                                <span className="uppercase">Ver menos</span>
                              </>
                            ) : (
                              <>
                                <BsPlusLg className="mr-2 inline-block grey-dark" size={17} />
                                <span className="uppercase">Ver mais</span>
                              </>
                            )}
                          </button>
                        )}
                      </ul>
                    </div>

                    {(activeStageObj as any)?._id !== '68af4da8458e527eaa788503' ? (
                      <>
                        {(activeStageObj as any)?.status === 'open' ||
                        (getActiveEvaluationStep &&
                          ((getActiveEvaluationStep === 5 &&
                            (activeStageObj as any)?.type &&
                            (activeStageObj as any)?.type === 'semester') ||
                            (getActiveEvaluationStep >= 4 &&
                              getActiveEvaluationStep < 5 &&
                              (activeStageObj as any)?.type &&
                              (activeStageObj as any)?.type === 'trimester') ||
                            (getActiveEvaluationStep < 4 &&
                              (activeStageObj as any)?.type &&
                              (activeStageObj as any)?.type === 'month'))) ||
                        (isDirectManager &&
                          (madUserCodEtapa(baseCtx, userInfo) === 1 ||
                            madUserCodEtapa(baseCtx, userInfo) === 3 ||
                            madUserCodEtapa(baseCtx, userInfo) === 4 ||
                            madUserCodEtapa(baseCtx, userInfo) === 5)) ? (
                          <div className="bg-maastricht-blue rounded-3xl p-8 space-y-4">
                            <p tabIndex={0} className="block text-white leading-none font-normal text-base">
                              Realizações da Fase {getMonthNameFromString((activeStageObj as any)?.title)}
                            </p>

                            {/* Abas Diretoria | MAD | Rede — mesmo lugar e layout do
                                monólito: dentro do card, logo abaixo do título da fase. */}
                            <FlowTabs
                              active="mad"
                              eligibility={eligibility}
                              hasRede={!!hasRedeMAD?.rede}
                              currentStage={currentStage}
                            />

                            {/* Seletor de PAR da dupla (COT-03). */}
                            {pairOptions.length > 1 && (
                              <div>
                                <p tabIndex={0} className="text-xs text-alice-blue block font-thin mb-2">
                                  Ver realizações de:
                                </p>
                                <CustomSelect
                                  placeholder="Selecione o par da dupla"
                                  options={pairOptions}
                                  value={pairOptions.find(o => o.value === pairSelected) ?? pairOptions[0]}
                                  onChange={(e: any) => setPairSelected(e?.value)}
                                  aria-label="Filtrar realizações por par da dupla"
                                />
                              </div>
                            )}

                            {activeStageObj && (activeStageObj as any).status === 'open' ? (
                              <>
                                <div>
                                  <p tabIndex={0} className="text-xs text-alice-blue block font-thin">
                                    O período de apreciação desta fase inicia em:
                                  </p>
                                  <div className="text-xs text-alice-blue block font-normal">
                                    <Countdown key="missao" deadline={new Date((activeStageObj as any).date_finish)} />
                                  </div>
                                </div>
                              </>
                            ) : null}

                            {getActiveEvaluationStep ||
                            (isDirectManager &&
                              (madUserCodEtapa(baseCtx, userInfo) === 1 ||
                                madUserCodEtapa(baseCtx, userInfo) === 3 ||
                                madUserCodEtapa(baseCtx, userInfo) === 4 ||
                                madUserCodEtapa(baseCtx, userInfo) === 5)) ? (
                              <>
                                {getActiveEvaluationStep &&
                                ((getActiveEvaluationStep === 5 &&
                                  madUserCodEtapa(baseCtx, userInfo) === 5 &&
                                  (activeStageObj as any)?.type &&
                                  (activeStageObj as any)?.type === 'semester') ||
                                  ((getActiveEvaluationStep === 4 || getActiveEvaluationStep === 4.1) &&
                                    (madUserCodEtapa(baseCtx, userInfo) === 4 || madUserCodEtapa(baseCtx, userInfo) === 5) &&
                                    (activeStageObj as any)?.type &&
                                    (activeStageObj as any)?.type === 'trimester') ||
                                  (getActiveEvaluationStep < 4 &&
                                    (madUserCodEtapa(baseCtx, userInfo) as number) < 4 &&
                                    (activeStageObj as any)?.type &&
                                    (activeStageObj as any)?.type === 'month') ||
                                  (isDirectManager &&
                                    (madUserCodEtapa(baseCtx, userInfo) === 1 ||
                                      madUserCodEtapa(baseCtx, userInfo) === 3 ||
                                      madUserCodEtapa(baseCtx, userInfo) === 4 ||
                                      madUserCodEtapa(baseCtx, userInfo) === 5))) ? (
                                  <>
                                    {!!isOriginalManager ? (
                                      <>
                                        {!!quotas.maxValidTasks && (
                                          <div className="space-x-3 flex flex-row items-center justify-between pt-4 border-t border-prussian-blue-2">
                                            <HiOutlineHandThumbUp className="text-xl" />
                                            <span tabIndex={0} className="flex-1 text-white text-xs leading-none font-thin">
                                              Total de realizações validadas
                                            </span>
                                            <div className="text-right text-yellow font-normal leading-none text-base">
                                              {quotas.validTasks?.length || 0}/{quotas.maxValidTasks || 0}
                                            </div>
                                          </div>
                                        )}

                                        {!!quotas.maxRejectedTasks && (
                                          <div className="space-x-3 flex flex-row items-center justify-between pt-4 border-t border-prussian-blue-2">
                                            <HiOutlineHandThumbDown className="text-xl" />
                                            <span tabIndex={0} className="flex-1 text-white text-xs leading-none font-thin">
                                              Total de realizações devolvidas
                                            </span>
                                            <div className="text-right text-yellow font-normal leading-none text-base">
                                              {quotas.rejectedTasks?.length || 0}/{quotas.maxRejectedTasks || 0}
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <></>
                                    )}

                                    {!!isOriginalManagerCod5 ||
                                    (madUserCodEtapa(baseCtx) &&
                                      madUserCodEtapa(baseCtx) !== 2 &&
                                      (madUserCodEtapa(baseCtx) as number) < 5 &&
                                      getActiveEvaluationStep &&
                                      getActiveEvaluationStep > 2) ||
                                    (madUserCodEtapa(baseCtx) &&
                                      madUserCodEtapa(baseCtx) !== 2 &&
                                      (madUserCodEtapa(baseCtx) as number) < 5 &&
                                      isDirectManager) ? (
                                      <>
                                        {!!quotas.maxDoubledTasks && (
                                          <div className="space-x-3 flex flex-row items-center justify-between pt-4 border-t border-prussian-blue-2">
                                            <FaCoins className="text-xl" />
                                            <span tabIndex={0} className="flex-1 text-white text-xs leading-none font-thin">
                                              Total de realizações bonificadas
                                            </span>
                                            <div className="text-right text-yellow font-normal leading-none text-base">
                                              {quotas.doubledTasks?.length || 0}/{quotas.maxDoubledTasks || 0}
                                            </div>
                                          </div>
                                        )}

                                        {madUserCodEtapa(baseCtx) !== 5 && !!quotas.maxPromotedTasks && (
                                          <div className="space-x-3 flex flex-row items-center justify-between pt-4 border-t border-prussian-blue-2">
                                            {madUserCodEtapa(baseCtx) === 5 &&
                                            (getActiveEvaluationStep === 5 ||
                                              playerInfo.funcao_localizacao === 3001 ||
                                              playerInfo.funcao_localizacao === 3002 ||
                                              !isOriginalManager) ? (
                                              <TfiMedallAlt className="text-xl" />
                                            ) : (
                                              <BiUpvote className="text-xl" />
                                            )}
                                            <span tabIndex={0} className="flex-1 text-white text-xs leading-none font-thin">
                                              {madUserCodEtapa(baseCtx) === 5 &&
                                              (getActiveEvaluationStep === 5 ||
                                                playerInfo.funcao_localizacao === 3001 ||
                                                playerInfo.funcao_localizacao === 3002 ||
                                                !isOriginalManager)
                                                ? 'Total de selos concedidos'
                                                : 'Total de realizações promovidas'}
                                            </span>
                                            <div className="text-right text-yellow font-normal leading-none text-base">
                                              {quotas.promotedTasks?.length || 0}/{quotas.maxPromotedTasks || 0}
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <></>
                                    )}

                                    {tasksProsseguirAssessor.length ? (
                                      <>
                                        <div className="space-x-3 flex flex-row items-center justify-between pt-4 border-t border-prussian-blue-2">
                                          <BiUpvote className={`text-xl rotate-90 transform`} />
                                          <span tabIndex={0} className="flex-1 text-white text-xs leading-none font-thin">
                                            Total de realizações enviadas para apreciação da diretoria
                                          </span>
                                          <div className="text-right text-yellow font-normal leading-none text-base">
                                            {
                                              tasksProsseguirAssessor.filter(item => {
                                                // Aprovação vale para a DUPLA (PIT-05/COT-11): quem aprovou pode
                                                // ter sido o parceiro — o card é da dupla, não do indivíduo.
                                                return (
                                                  (item.prosseguir_who_approved &&
                                                    (item.prosseguir_who_approved === internalUser?.extra.id ||
                                                      baseCtx.duplaIds.includes(String(item.prosseguir_who_approved)))) ||
                                                  (Array.isArray(item.prosseguir_managers) &&
                                                    item.prosseguir_managers.length >= 2 &&
                                                    item.prosseguir_managers[0] === playerInfo._id)
                                                );
                                              }).length
                                            }
                                            /{tasksProsseguirAssessor.length}
                                          </div>
                                        </div>
                                      </>
                                    ) : (
                                      <></>
                                    )}

                                    {madUserCodEtapa(baseCtx) === 5 &&
                                    (activeStageObj as any)?.type &&
                                    (activeStageObj as any)?.type === 'semester' ? (
                                      <>
                                        <div className="space-x-3 flex flex-row items-center justify-between pt-4 border-t border-prussian-blue-2">
                                          <PiThumbsUpLight className="text-xl" />
                                          <span tabIndex={0} className="flex-1 text-white text-xs leading-none font-thin">
                                            Total de realizações Valeu
                                          </span>
                                          <div className="text-right text-yellow font-normal leading-none text-base">
                                            {quotas.valeuTasks?.length || 0}/{quotas.maxValeuTasks || 0}
                                          </div>
                                        </div>

                                        <div className="space-x-3 flex flex-row items-center justify-between pt-4 border-t border-prussian-blue-2">
                                          <PiHandsClappingLight className="text-xl" />
                                          <span tabIndex={0} className="flex-1 text-white text-xs leading-none font-thin">
                                            Total de realizações Mandou Bem
                                          </span>
                                          <div className="text-right text-yellow font-normal leading-none text-base">
                                            {quotas.mandouBemTasks?.length || 0}/{quotas.maxMandouBemTasks || 0}
                                          </div>
                                        </div>

                                        <div className="space-x-3 flex flex-row items-center justify-between pt-4 border-t border-prussian-blue-2">
                                          <FaRegGrinStars className="text-xl" />
                                          <span tabIndex={0} className="flex-1 text-white text-xs leading-none font-thin">
                                            Total de realizações Uau
                                          </span>
                                          <div className="text-right text-yellow font-normal leading-none text-base">
                                            {quotas.uauTasks?.length || 0}/{quotas.maxUauTasks || 0}
                                          </div>
                                        </div>
                                      </>
                                    ) : null}

                                    <div className="space-x-3 flex flex-row items-center justify-between pt-4 border-t border-prussian-blue-2">
                                      <BsChatText className="text-xl" />
                                      <span tabIndex={0} className="flex-1 text-white text-xs leading-none font-thin">
                                        Total de feedbacks concedidos
                                      </span>
                                      <div className="text-right text-yellow font-normal leading-none text-base">
                                        {quotas.feedbackTasks}/{quotas.maxValidTasks}
                                      </div>
                                    </div>
                                  </>
                                ) : null}
                              </>
                            ) : null}
                          </div>
                        ) : !!hasRedeMAD?.mad || !!hasRedeMAD?.rede || canSwitchFlow ? (
                          <div className="bg-maastricht-blue rounded-3xl p-8 space-y-4">
                            {/* Fora da janela: abas + seletor de par. */}
                            <FlowTabs
                              active="mad"
                              eligibility={eligibility}
                              hasRede={!!hasRedeMAD?.rede}
                              currentStage={currentStage}
                            />

                            {pairOptions.length > 1 && (
                              <div>
                                <p tabIndex={0} className="text-xs text-alice-blue block font-thin mb-2">
                                  Ver realizações de:
                                </p>
                                <CustomSelect
                                  placeholder="Selecione o par da dupla"
                                  options={pairOptions}
                                  value={pairOptions.find(o => o.value === pairSelected) ?? pairOptions[0]}
                                  onChange={(e: any) => setPairSelected(e?.value)}
                                  aria-label="Filtrar realizações por par da dupla"
                                />
                              </div>
                            )}
                          </div>
                        ) : null}
                      </>
                    ) : null}
                  </>
                )}
              </Box>
            </div>

            {!!getFullCalendar && (activeStageObj as any)?._id !== '68af4da8458e527eaa788503' && (
              <div className="pt-3">
                <h2 tabIndex={0} className="text-xl text-white block mb-6 font-semibold">
                  Calendário
                </h2>

                <Box className="relative bg-prussian-blue border-electric-blue p-4 lg:p-8 border-opacity-100 bg-opacity-100 w-full rounded-[20px]">
                  {loading ? (
                    <div className="grid grid-cols-1 gap-3">
                      <ArticlePlaceholder />
                    </div>
                  ) : (
                    <>
                      <div className="bg-maastricht-blue rounded-3xl p-8 space-y-4">
                        <div className="font-thin">
                          {getFullCalendar.slice(0, calendarNumberOfElements).map((item, idx) => (
                            <p
                              tabIndex={0}
                              className={idx === 0 ? 'm-0' : `pt-2 mt-2 border-t border-prussian-blue-2`}
                              key={item.title}
                            >
                              <span className="text-yellow font-light">
                                {format(item.start, 'dd/MM')} a {format(subMinutes(item.end, 10), 'dd/MM')}:
                              </span>{' '}
                              {item.stage} - <span className="text-blue-light font-light">{item.period}</span>
                            </p>
                          ))}

                          {!!getFullCalendar && getFullCalendar?.length > (calendarElementsIndexOf as number) && (
                            <button
                              className="mt-4 mx-auto flex items-center text-grey-light justify-center lg:justify-start"
                              onClick={() =>
                                !calendarShowMore
                                  ? setCalendarNumberOfElements(calendarElementsIndexOf as number)
                                  : setCalendarNumberOfElements(calendarNumberOfElements + 5)
                              }
                            >
                              {!calendarShowMore ? (
                                <>
                                  <BiMinus className="mr-2 inline-block grey-dark" size={17} />
                                  <span className="uppercase">Ver menos</span>
                                </>
                              ) : (
                                <>
                                  <BsPlusLg className="mr-2 inline-block grey-dark" size={17} />
                                  <span className="uppercase">Ver mais</span>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </Box>
              </div>
            )}

          </div>

          <div className="lg:w-[64.8%]">
            <div className="mb-3 flex items-center justify-between mb-6">
              <h2 tabIndex={0} className="text-xl text-white block m-0 font-semibold flex-1">
                Realizações
                {!loading && filteredData ? ` da Fase ${getMonthNameFromString((activeStageObj as any)?.title)}` : ''}
                {!loading && tasksByFilter?.length ? <em> ({tasksByFilter?.length})</em> : ''}
              </h2>

              {tasksByFilter && tasksByFilter?.length > 0 && <ExportButton data={tasksByFilter} />}
            </div>

            {!loading && filteredData && filteredData?.length > 0 && (
              <>
                <div className="w-full flex justify-end mb-3 space-x-3">
                  {Array.isArray(tasksStatus) && !!tasksStatus.length && (
                    <CustomSelect
                      placeholder="Filtrar por feedback"
                      className="w-[35%]"
                      options={feedbackFilterOptions}
                      value={selectedFeedbackOption}
                      onChange={(e: any) => setSelectedFeedbackStatus(e?.value)}
                    />
                  )}

                  {Array.isArray(tasksStatus) && !!tasksStatus.length && (
                    <CustomSelect
                      placeholder="Filtrar por status"
                      className="w-[35%]"
                      options={taskStatusOptions}
                      value={selectedTaskStatusOption}
                      onChange={(e: any) => setSelectedTasksStatus(e?.value)}
                      aria-label="Filtrar realizações por status"
                    />
                  )}

                  <input
                    value={searchText || ''}
                    onChange={e => setSearchText(e.target.value)}
                    className={`text-white bg-maastricht-blue min-h-[38px] w-[35%] py-2 px-4 rounded-md leading-none text-sm font-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow`}
                    placeholder="Filtrar por texto"
                    aria-label="Filtrar realizações por texto"
                  />
                </div>
              </>
            )}

            <Box className="relative bg-prussian-blue border-electric-blue p-4 lg:p-8 border-opacity-100 bg-opacity-100 w-full rounded-[20px] space-y-6">
              {loading ? (
                <div className="grid grid-cols-1 gap-3">
                  <ArticlePlaceholder />
                </div>
              ) : tasksByFilter?.length ? (
                tasksByFilter?.map(item => (
                  <article
                    key={item._id}
                    aria-label={`Realização: ${item.title || item.description || item.result}`}
                    className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow"
                  >
                    <TaskItemMad
                      editable={canEdit(ctx, item)}
                      bonus={
                        !!bonus?.hasBonus &&
                        !bonus?.isUsed &&
                        quotas.promotedTasks.length >= quotas.maxPromotedTasks &&
                        (!Array.isArray(item.prosseguir_managers) ||
                          !item.prosseguir_managers.length ||
                          !item.prosseguir_managers.includes(playerInfo._id))
                      }
                      promote={canPromote(ctx, item, item)}
                      double={canDouble(ctx, item)}
                      award={canAward(ctx, item)}
                      onClearStatus={(item: any, status: any) => console.log('remove status', status, item)}
                      data={item}
                      manager={gestor}
                      managerType={CheckManagerType({
                        user: { ...playerInfo, cod_etapa: madUserCodEtapa(ctx, playerInfo) },
                        item,
                      })}
                      playerInfo={playerInfo}
                      canEvaluate={canEvaluate(ctx, item)}
                      canUpdateIndicator={canUpdateIndicator(item, playerInfo)}
                      onUpdateIndicator={() => setIndicatorTask({ id: item._id, owner: item.owner })}
                      isParticipantsApprover={canApproveParticipantsCount(playerInfo, item)}
                      isPitStopAssessor={isPitStopAssessor(ctx, item)}
                      onPromote={handlePromote}
                      onAccept={handleAccept}
                      onReject={handleReject}
                      onDouble={handleDoubled}
                      onStop={handleStop}
                      onFeedback={() => loadData(item._id)}
                      onReload={() => loadData(item._id)}
                      onAward={handleAward}
                      cycle={activeStageObj}
                      activeEvaluationStep={getActiveEvaluationStep}
                      taskStep={getTaskStepLocal(item)}
                      playerStep={getPlayerStep(ctx, item)}
                      onReset={handleReset}
                      reset={canReset(ctx, item)}
                      getAction={getAction}
                      clearGetAction={() => setGetAction(null)}
                    />
                  </article>
                ))
              ) : (
                <div
                  className="flex justify-center items-center flex-col space-y-2 lg:space-y-8 bg-maastricht-blue-3 rounded-[20px] p-8 text-center"
                  role="alert"
                >
                  <img src={ImgFeedback} className="max-w-[150px]" alt="" />
                  <p tabIndex={0} className="text-lg block text-center font-thin text-white leading-loose tracking-tight">
                    {(activeStageObj as any)?._id === '68af4da8458e527eaa788503' ? (
                      <strong className="block text-center text-yellow font-semibold text-xl">
                        Não há Realizações Destaque nesta fase.
                      </strong>
                    ) : (
                      <>
                        {Array.isArray(filteredData) &&
                        filteredData.length &&
                        (searchText?.length ||
                          (typeof selectedFeedbackStatus === 'string' &&
                            selectedFeedbackStatus.length &&
                            selectedFeedbackStatus.toLowerCase() !== 'selecione')) ? (
                          <strong className="block text-center text-yellow font-semibold text-xl">
                            Sem Realizações Destaque para esse filtro.
                          </strong>
                        ) : (
                          <strong className="block text-center text-yellow font-semibold text-xl">
                            Sua equipe
                            {(activeStageObj as any)?.status === 'open' ? ' ainda ' : ' '}não cadastrou Realizações Destaque nesta
                            fase.
                          </strong>
                        )}
                      </>
                    )}
                  </p>
                </div>
              )}
            </Box>
          </div>
        </div>
      </main>

      <Outlet />

      {!!selectedDupla && (
        <SelectDupla
          player={selectedDupla}
          closeModal={() => {
            setSelectedDupla(undefined);
          }}
          onSubmit={option => {
            const task = selectedDupla.task;
            setSelectedDupla(undefined);

            const selection = resolveDuplaSelection(option);
            if (selection) {
              functionsObject[functionDupla](task, selection);
            }
          }}
        />
      )}

      {!!modalCod5?._id && (
        <ModalCod5
          task={modalCod5}
          playerInfo={playerInfo}
          onSubmit={handleSubmitCod5}
          onClose={() => setModalCod5(undefined)}
          award={canAward(ctx, modalCod5, true)}
          userCodEtapa={madUserCodEtapa(ctx)}
        />
      )}

      {!!showConcurrentActionModal && (
        <ModalAlert
          description={showConcurrentActionModal}
          onClose={() => {
            setShowConcurrentActionModal(undefined);
          }}
        />
      )}

      {!!indicatorTask && (
        <ModalChangeIndicator
          id={indicatorTask.id}
          owner={indicatorTask.owner}
          callback={updated => {
            const taskId = indicatorTask.id;
            setIndicatorTask(undefined);

            if (updated) loadData(taskId);
          }}
        />
      )}
    </>
  );
}
